<script lang="ts">
	import type { ItemList, Product } from "schema-dts";
	import calendarIcon from "@core/assets/icons/calendar.svg?raw";
	import magnifierIcon from "@core/assets/icons/magnifier.svg?raw";
	import type { Procedure } from "@core/schema/Procedure.js";
	import type { UploadedFile } from "@core/schema/UploadedFile.js";
	import { getProcedureDetailPath } from "@core/schema/paths/getProcedureDetailPath.js";
	import { getUploadedFilePath } from "@core/utils/getUploadedFilePath.js";
	import { getGlobalSettings } from "../../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import Formatted from "../common/Formatted.svelte";
	import Link from "../common/Link.svelte";
	import MediaWithHoverVideo from "../common/MediaWithHoverVideo.svelte";
	import StructuredData from "../common/StructuredData.svelte";
	import { ButtonVariant } from "../common/getButtonClass.js";

	interface Props {
		procedures: Procedure[];
	}

	const { procedures }: Props = $props();
	const { tenant } = getGlobalTemplateProps();
	const { FILE_BUCKET_URL_MAP } = getGlobalSettings();

	function getVideoUrl(video: unknown): string | undefined {
		const file = video as UploadedFile | undefined;
		if (!file) {
			return undefined;
		}
		return getUploadedFilePath(file, FILE_BUCKET_URL_MAP);
	}

	function formatDuration(minutes: number): string {
		return `(${minutes} min)`;
	}

	function formatPrice(price: number): string {
		return `${price.toLocaleString("cs-CZ")} Kč`;
	}

	const proceduresListData: ItemList = {
		"@type": "ItemList",
		itemListElement: procedures.map((procedure, index) => ({
			"@type": "ListItem",
			position: index + 1,
			item: {
				"@type": "Product",
				name: procedure.title.replace("nl[[]]", " "),
				description: procedure.description,
				offers: {
					"@type": "Offer",
					price: String(procedure.price),
					priceCurrency: "CZK",
					availability: "https://schema.org/InStock",
					url: `https://headspa-napohodu.cz${getProcedureDetailPath(tenant.locale, procedure.urlSlug)}`,
				},
				category: "Head Spa Procedura",
			} satisfies Product,
		})),
	};

	interface VideoComponent {
		play(): void;
		stop(): void;
	}
	const videoRefs: Record<number, VideoComponent | undefined> = {};

	function handleCardMouseEnter(index: number): void {
		const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches;
		const ref = videoRefs[index];
		if (!isMobile && ref !== undefined) {
			ref.play();
		}
	}

	function handleCardMouseLeave(index: number): void {
		const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches;
		const ref = videoRefs[index];
		if (!isMobile && ref !== undefined) {
			ref.stop();
		}
	}

	// Helper to get first image URL - uses unknown due to svelte-check type inference bug
	function getFirstImageUrl(images: unknown[]): string | null {
		if (images.length > 0) {
			return getUploadedFilePath(images[0] as UploadedFile, FILE_BUCKET_URL_MAP);
		}
		return null;
	}
</script>

<StructuredData data={proceduresListData} />

<section class="flex flex-col items-start bg-ivory-200 full-width">
	<div class="flex flex-col justify-center items-center gap-8 sm:gap-12 md:gap-16 w-full container-center">
		<h1 class="text-center w-full max-w-[57.5rem]">
			<Formatted text="Naše" />
			<span class="relative inline-block">
				<Formatted text="pohodové" />
				<span class="absolute bottom-0 left-0 h-0.5 md:h-1 w-full bg-current"></span>
			</span>
			<br />
			<Formatted text="procedury" />
		</h1>

		<div class="flex flex-col items-start gap-4 w-full max-w-[74rem] px-4 xl:px-0">
			{#each procedures as procedure, index (procedure.id)}
				{@const imageUrl = getFirstImageUrl(procedure.images)}
				{@const videoUrl = getVideoUrl(procedure.video)}
				<div
					class="flex flex-col md:flex-row items-center p-4 sm:p-8 md:p-10 lg:p-16 gap-4 sm:gap-6 md:gap-10 lg:gap-16 w-full lg:h-[39.875rem] bg-ivory-100 border border-ivory-300 shadow-[0_1.5rem_.5rem_-1rem_rgba(0,0,0,0.05)] rounded-[2rem] sm:rounded-[3rem] md:rounded-[4rem] lg:rounded-[6rem]"
					role="group"
					onmouseenter={(): void => {
						handleCardMouseEnter(index);
					}}
					onmouseleave={(): void => {
						handleCardMouseLeave(index);
					}}
				>
					<a
						href={getProcedureDetailPath(tenant.locale, procedure.urlSlug)}
						class="block w-full md:w-[20rem] lg:w-[30rem] aspect-[480/510] rounded-[1.5rem] sm:rounded-[2rem] overflow-hidden flex-shrink-0 self-stretch"
					>
						<MediaWithHoverVideo
							bind:this={videoRefs[index]}
							imageSrc={imageUrl ?? ""}
							videoSrc={videoUrl}
							alt={procedure.title}
							imageWidth={480}
							imageHeight={510}
							class="w-full h-full object-cover rounded-[1.5rem] sm:rounded-[2rem]"
							wrapperClass="w-full h-full"
							playOnScroll={true}
							eager
						/>
					</a>

					<div
						class="flex flex-col items-start py-0 sm:py-2 md:py-4 lg:py-16 gap-4 sm:gap-5 md:gap-6 lg:gap-8 w-full lg:w-[32rem] flex-grow"
					>
						<div class="flex flex-col items-start gap-3 sm:gap-4 lg:gap-6 w-full">
							<a href={getProcedureDetailPath(tenant.locale, procedure.urlSlug)} class="no-underline">
								<h3
									class="font-heading text-base sm:text-lg lg:text-[2rem] font-bold text-hazelnut-500 w-full leading-[110%]"
								>
									<Formatted text={procedure.title} />
									<br />
									<span class="inline-block">
										{formatDuration(procedure.duration)}
									</span>
								</h3>
							</a>

							<p class="text-xs sm:text-sm lg:text-base font-medium text-midnight w-full leading-[140%]">
								<Formatted text={procedure.description} />
							</p>
						</div>

						<div class="flex flex-row flex-wrap items-start content-start gap-1 lg:gap-1 w-full">
							{#each procedure.tags.slice(0, 8) as tag, tagIndex (tag)}
								<span
									class="justify-center items-center px-2 sm:px-3 py-0.5 sm:py-1 rounded-[2rem] sm:rounded-[2.5rem] text-[10px] sm:text-xs font-medium leading-[0.875rem] sm:leading-[.9375rem] uppercase border border-[#F7CFCF] text-midnight {tagIndex >=
									5
										? 'hidden lg:flex'
										: 'flex'}"
								>
									<Formatted text={tag} />
								</span>
							{/each}
							{#if procedure.tags.length > 5}
								<span
									class="flex lg:hidden justify-center items-center px-2 py-0.5 rounded-[2rem] text-[10px] font-medium leading-[0.875rem] border border-[#F7CFCF] text-midnight"
								>
									a další…
								</span>
							{/if}
							{#if procedure.tags.length > 8}
								<span
									class="hidden lg:flex justify-center items-center sm:px-3 sm:py-1 sm:rounded-[2.5rem] sm:text-xs font-medium sm:leading-[.9375rem] border border-[#F7CFCF] text-midnight"
								>
									a další…
								</span>
							{/if}
						</div>

						<div class="flex flex-col items-start gap-2 w-full">
							<p class="text-base sm:text-md lg:text-[1.5rem] font-bold text-midnight w-full leading-[1.5]">
								{formatPrice(procedure.price)}
							</p>

							<div class="flex flex-col sm:flex-row items-start gap-2 flex-wrap w-full">
								<Link
									href="https://head-spa-na-pohodu.reservio.com/"
									target="_blank"
									variant={ButtonVariant.PrimarySmall}
									iconStart={calendarIcon}>Rezervovat termín</Link
								>

								<Link
									href={getProcedureDetailPath(tenant.locale, procedure.urlSlug)}
									variant={ButtonVariant.SecondarySmall}
									iconStart={magnifierIcon}
								>
									detail procedury
								</Link>
							</div>
						</div>
					</div>
				</div>
			{/each}
		</div>
	</div>
</section>
